import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { hasuraClient } from '../../../utils/graphqlClient';
import { GET_LEAVE_REQUESTS_QUERY } from '../../../graphql/queries/leaveRequests';
import { formatDateTR } from '../../../utils/dateUtils';

const safe = (value) => (value ? String(value) : '');

const LeaveRequestPrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [leaveRequest, setLeaveRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasPrintedRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const where = { id: { _eq: parseInt(id, 10) } };
        const data = await hasuraClient.query(GET_LEAVE_REQUESTS_QUERY, { where });
        const lr = (data.leave_requests || [])[0];
        if (!lr) {
          setLeaveRequest(null);
          return;
        }

        setLeaveRequest({
          id: lr.id,
          startDate: lr.start_date,
          endDate: lr.end_date,
          leaveTypeName: lr.leaveType?.leave_type_name || lr.leave_type,
          description: lr.leave_request_description || '',
          duration: lr.duration,
          status: lr.status,
          user: lr.user,
        });
      } catch (e) {
        setLeaveRequest(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  useEffect(() => {
    if (loading || !leaveRequest) return;

    // React Strict Mode'da effect'ler iki kez çalıştığı için
    // sadece ilkinde print alalım
    if (hasPrintedRef.current) return;
    hasPrintedRef.current = true;

    // Küçük bir delay ile print diyalogunu aç
    setTimeout(() => {
      window.print();
    }, 300);
  }, [loading, leaveRequest]);

  // PDF ismi için basit slug üret
  const normalizeText = (text) =>
    text
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '');

  const firstName = safe(leaveRequest?.user?.first_name);
  const lastName = safe(leaveRequest?.user?.last_name);
  const leaveTypeSlug = normalizeText(leaveRequest?.leaveTypeName || 'izin');
  const nameSlug = `${normalizeText(firstName)}${normalizeText(lastName)}`;
  const pdfTitle = `istanbulproje_${nameSlug}_${leaveTypeSlug}.pdf`;

  useEffect(() => {
    if (!pdfTitle) return;
    const previousTitle = document.title;
    document.title = pdfTitle;
    return () => {
      document.title = previousTitle;
    };
  }, [pdfTitle]);

  if (loading || !leaveRequest) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const today = formatDateTR(new Date());
  const startDate = formatDateTR(leaveRequest.startDate);
  const endDate = formatDateTR(leaveRequest.endDate);
  const title =
    (leaveRequest.leaveTypeName || '').toUpperCase() || 'İZİN FORMU';

  const handleBack = () => {
    const params = new URLSearchParams(location.search);
    const from = params.get('from');

    if (from === 'profile') {
      navigate('/profile');
    } else {
      navigate('/applications/leave-requests');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', color: '#222' }}>
      {/* Geri butonu - sadece ekranda, print'te görünmez */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 10,
          '@media print': {
            display: 'none',
          },
        }}
      >
        <Button variant="outlined" size="small" color="inherit" onClick={handleBack}>
          {t('common.back', 'Geri Dön')}
        </Button>
      </Box>

      {/* Sadece form alanını merkezde göster */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 3,
        }}
      >
      <Box
        sx={{
          width: '100%',
          maxWidth: 840,
          border: '1px solid #888',
          p: 2,
          bgcolor: '#ffffff',
          fontFamily: `'Courier New', monospace`,
          color: '#222',
        }}
      >
        {/* Başlık */}
        <Box
          sx={{
            borderBottom: '2px solid #000',
            pb: 1,
            mb: 1,
            display: 'flex',
            justifyContent: 'space-between',
          }}
          >
          <Typography
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 1.5,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              color: '#333',
            }}
          >
            Tarih: {today}
          </Typography>
        </Box>

        {/* Personel Bilgileri Başlık */}
        <Box
          sx={{
            borderBottom: '2px solid #000',
            pb: 0.5,
            mb: 0.5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              letterSpacing: 1,
              color: '#333',
            }}
          >
            PERSONELİN
          </Typography>
        </Box>

        {/* Satırlar */}
        <Box>
          {[
            {
              label: 'Adı ve Soyadı',
              value: `${safe(leaveRequest.user?.first_name)} ${safe(leaveRequest.user?.last_name)}`,
            },
            {
              label: 'İznin Başlama Tarihi',
              value: startDate,
            },
            {
              label: 'İzin Gün Sayısı',
              value: safe(leaveRequest.duration),
            },
            {
              label: 'İşe Başlama Tarihi',
              value: endDate,
            },
            {
              label: 'Açıklama',
              value: safe(leaveRequest.description),
            },
          ].map((row, index) => (
            <Box
              key={row.label}
              sx={{
                display: 'flex',
                borderBottom: '1px solid #000',
                py: 0.75,
              }}
            >
              <Box sx={{ width: '35%', pr: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#333',
                  }}
                >
                  {row.label}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: '#222',
                  }}
                >
                  {row.value}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Onaylar */}
        <Box
          sx={{
            borderTop: '1px solid #888',
            mt: 2,
            pt: 1,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontWeight: 600, mb: 4, letterSpacing: 1, color: '#333' }}>
            ONAYLAR
          </Typography>
          <Typography sx={{ fontWeight: 500, color: '#333' }}>
            Personelin İmzası
          </Typography>
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default LeaveRequestPrintPage;


